import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { memberApi } from '../services/memberApi';
import nausLogo from '../assets/naus-logo.jpeg';

// Helper function to get full image URL (handles both Cloudinary and local URLs)
const getImageUrl = (url, baseUrl) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `${baseUrl}/uploads/${url}`;
};

const Verify = () => {
  const { membershipNumber } = useParams();
  const [loading, setLoading] = useState(true);
  const [verified, setVerified] = useState(false);
  const [member, setMember] = useState(null);
  const [error, setError] = useState('');

  const API_BASE_URL = 'https://naus-backend.onrender.com';

  useEffect(() => {
    verifyMember();
  }, [membershipNumber]);

  const verifyMember = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await memberApi.verifyMember(membershipNumber);

      if (response.data.verified) {
        setVerified(true);
        setMember(response.data.member);
      } else {
        setVerified(false);
        setError(response.data.message || 'Member not found');
      }
    } catch (err) {
      console.error('Verification error:', err);
      setVerified(false);
      setError(err.response?.data?.message || 'Unable to verify membership');
    } finally {
      setLoading(false);
    }
  };

  // Loading State
  if (loading) {
    return (
      <div className="section" style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid var(--primary-green)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }} />
          <p style={{ color: '#666' }}>Verifying membership...</p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  // Not Found / Error State
  if (!verified || error) {
    return (
      <div className="section" style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="container">
          <div style={{
            maxWidth: '500px',
            margin: '0 auto',
            textAlign: 'center',
            backgroundColor: 'var(--white)',
            padding: '3rem 2rem',
            borderRadius: '16px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)'
          }}>
            {/* Error Icon */}
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              backgroundColor: '#f8d7da',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem'
            }}>
              <span style={{ fontSize: '3rem' }}>&#10060;</span>
            </div>

            <h1 style={{ color: '#dc3545', marginBottom: '1rem' }}>
              Verification Failed
            </h1>

            <p style={{ color: '#666', marginBottom: '2rem', fontSize: '1.1rem' }}>
              {error || 'The membership number provided could not be verified. Please check and try again.'}
            </p>

            <p style={{ color: '#999', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
              Membership Number: <strong>{membershipNumber}</strong>
            </p>

            <Link
              to="/"
              style={{
                display: 'inline-block',
                padding: '0.75rem 2rem',
                backgroundColor: 'var(--primary-green)',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '8px',
                fontWeight: '500'
              }}
            >
              Go to Homepage
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Verified State
  return (
    <div className="section" style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="container">
        <div style={{
          maxWidth: '500px',
          margin: '0 auto',
          textAlign: 'center',
          backgroundColor: 'var(--white)',
          borderRadius: '16px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden'
        }}>
          {/* Header with Logo */}
          <div style={{
            backgroundColor: 'var(--primary-green)',
            padding: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1rem'
          }}>
            <img
              src={nausLogo}
              alt="NAUS Logo"
              style={{
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                border: '2px solid white'
              }}
            />
            <div style={{ textAlign: 'left', color: 'white' }}>
              <p style={{ margin: 0, fontSize: '0.75rem', opacity: 0.9 }}>NIGERIAN ASSOCIATION OF</p>
              <p style={{ margin: 0, fontWeight: 'bold', fontSize: '1rem' }}>UROLOGICAL SURGEONS</p>
            </div>
          </div>

          {/* Verification Badge */}
          <div style={{ padding: '2rem 2rem 1rem' }}>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              backgroundColor: member.isActive ? '#d4edda' : '#fff3cd',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem',
              border: `4px solid ${member.isActive ? '#28a745' : '#ffc107'}`
            }}>
              <span style={{ fontSize: '3rem' }}>
                {member.isActive ? '\u2713' : '\u26A0'}
              </span>
            </div>

            <div style={{
              display: 'inline-block',
              padding: '0.5rem 1.5rem',
              backgroundColor: member.isActive ? '#28a745' : '#ffc107',
              color: member.isActive ? 'white' : '#856404',
              borderRadius: '20px',
              fontWeight: 'bold',
              fontSize: '1rem',
              marginBottom: '1.5rem'
            }}>
              {member.isActive ? 'VERIFIED MEMBER' : 'INACTIVE MEMBER'}
            </div>
          </div>

          {/* Member Photo */}
          <div style={{
            width: '120px',
            height: '120px',
            borderRadius: '50%',
            overflow: 'hidden',
            margin: '0 auto 1rem',
            border: '4px solid var(--primary-green)',
            backgroundColor: '#f0f0f0'
          }}>
            {member.profilePhoto ? (
              <img
                src={getImageUrl(member.profilePhoto, API_BASE_URL)}
                alt={`${member.firstName} ${member.lastName}`}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div style={{
              width: '100%',
              height: '100%',
              display: member.profilePhoto ? 'none' : 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '3rem',
              color: '#ccc'
            }}>
              &#128100;
            </div>
          </div>

          {/* Member Details */}
          <div style={{ padding: '0 2rem 2rem' }}>
            <h2 style={{ margin: '0 0 0.5rem', color: 'var(--primary-green)' }}>
              {member.firstName} {member.middleName || ''} {member.lastName}
            </h2>

            {member.areaOfSpecialty && (
              <p style={{ margin: '0 0 1rem', color: '#666' }}>
                {member.areaOfSpecialty}
              </p>
            )}

            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '1.5rem',
              borderRadius: '12px',
              textAlign: 'left'
            }}>
              <div style={{
                display: 'grid',
                gap: '1rem'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', paddingBottom: '0.75rem' }}>
                  <span style={{ color: '#666' }}>Membership No.</span>
                  <strong style={{ color: '#333' }}>{member.membershipNumber}</strong>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', paddingBottom: '0.75rem' }}>
                  <span style={{ color: '#666' }}>Status</span>
                  <strong style={{ color: member.isActive ? '#28a745' : '#ffc107' }}>
                    {member.isActive ? 'Active' : 'Inactive'}
                  </strong>
                </div>

                {member.currentPractice && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', paddingBottom: '0.75rem' }}>
                    <span style={{ color: '#666' }}>Institution</span>
                    <strong style={{ color: '#333', textAlign: 'right', maxWidth: '60%' }}>{member.currentPractice}</strong>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#666' }}>Member Since</span>
                  <strong style={{ color: '#333' }}>
                    {member.joinedDate ? new Date(member.joinedDate).getFullYear() : 'N/A'}
                  </strong>
                </div>
              </div>
            </div>

            {/* Verification Timestamp */}
            <p style={{
              margin: '1.5rem 0 0',
              fontSize: '0.8rem',
              color: '#999'
            }}>
              Verified on {new Date().toLocaleDateString('en-NG', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>

          {/* Footer */}
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '1rem',
            borderTop: '1px solid #eee'
          }}>
            <Link
              to="/"
              style={{
                color: 'var(--primary-green)',
                textDecoration: 'none',
                fontWeight: '500',
                fontSize: '0.9rem'
              }}
            >
              Visit NAUS Website &#8594;
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Verify;
